// This is a web-only gl2025 component that uses inline styles and HTML elements
// Following the pattern from Button.gl2025.js
/* eslint-disable custom/no-web-only-html-tags */

import { createElement as rc } from 'react';
import tableStyle, {
    COLUMN_WIDTHS,
    HEADER_DIMENSIONS,
    INPUT_SIZES,
    getCheckboxColors
} from 'lib_ui-primitives/src/styling/themes/gl2025/tables';
import sizes from 'lib_ui-primitives/src/styling/themes/gl2025/sizes';
import { check, minus } from 'lib_ui-primitives/src/components/customIcons2';
import BaseIcon from 'lib_ui-primitives/src/components/Icons/BaseIconAdapterGl2025';
import getNestedPatchDetailColumnHNodes from '../columns/PatchDetail/getNestedPatchDetailColumnHNodes';
import { useHeaderRowLogic } from './HeaderRow.gl2025.shared';

/**
 * HeaderRow component for gl2025 theme
 * Renders table header with sort indicators and checkboxes
 *
 * - Sort indicators using BaseIcon
 * - Supports two-grid layout with pinned columns
 */
export default function HeaderRow(props) {
    // Use shared business logic
    const {
        showCheckbox,
        darkMode,
        styles,
        columnHNodes,
        columnWidths,
        borderRadiusTopLeft,
        borderRadiusTopRight,
        invertSelection,
        onChangeEverything,
        selectionDisabled,
        singleRowSelect,
        checkboxState,
        getSortState,
        handleColumnClick,
        handleColumnWidthChange
    } = useHeaderRowLogic(props, tableStyle);

    // Main header container - NO padding on container, cells handle their own padding
    const containerStyle = styles.default.container;

    // Build border-radius based on which corners should be rounded
    // Format: top-left top-right bottom-right bottom-left
    // This is needed for single & two-grid layouts to work
    const topLeftRadius = borderRadiusTopLeft ? `${HEADER_DIMENSIONS.borderRadius}px` : '0';
    const topRightRadius = borderRadiusTopRight ? `${HEADER_DIMENSIONS.borderRadius}px` : '0';
    const borderRadius = `${topLeftRadius} ${topRightRadius} 0 0`;

    const headerContainerStyle = {
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: `${HEADER_DIMENSIONS.height}px`,
        boxSizing: 'border-box', // Include borders in height
        backgroundColor: containerStyle.backgroundColor,
        borderBottom: `${containerStyle.borderBottomWidth} solid ${containerStyle.borderBottomColor}`,
        borderTop: `${containerStyle.borderTopWidth} solid ${styles.default.container.borderTopColor}`,
        borderRadius, // Conditionally rounded top corners
        position: 'sticky',
        top: 0,
        zIndex: 20
    };

    // Checkbox column style
    // Note: Border and border-radius handled by parent headerContainerStyle
    // No explicit height - inherits from parent flex container
    const checkboxColumnStyle = {
        width: `${sizes.fixedSpacing['6xl']}px`,
        minWidth: `${sizes.fixedSpacing['6xl']}px`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${HEADER_DIMENSIONS.padding.vertical}px`,
        flexShrink: 0
        // No backgroundColor - let parent container's rounded corners show through
    };

    const renderCheckboxCell = () => {
        if (!showCheckbox) return null;

        const { isChecked, hasState } = checkboxState;
        const iconPath = isChecked ? check : minus;

        const checkboxColors = getCheckboxColors(darkMode, hasState);
        const checkboxInputStyle = {
            cursor: 'pointer',
            width: `${INPUT_SIZES.checkbox.width}px`,
            height: `${INPUT_SIZES.checkbox.height}px`,
            margin: 0,
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            border: `${INPUT_SIZES.checkbox.borderWidth}px solid`,
            borderColor: checkboxColors.borderColor,
            borderRadius: `${INPUT_SIZES.checkbox.borderRadius}px`,
            backgroundColor: checkboxColors.backgroundColor,
            backgroundImage: hasState
                ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='${iconPath}'/%3E%3C/svg%3E")`
                : 'none',
            backgroundSize: `${INPUT_SIZES.checkbox.iconSize}px ${INPUT_SIZES.checkbox.iconSize}px`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            flexShrink: 0
        };

        return rc(
            'div',
            { style: checkboxColumnStyle, key: 'checkbox-header' },
            !singleRowSelect &&
                rc('input', {
                    type: 'checkbox',
                    disabled: selectionDisabled,
                    checked: invertSelection === true,
                    onChange: () => onChangeEverything(!invertSelection),
                    style: checkboxInputStyle
                })
        );
    };

    const renderHeaderColumn = (column, columnIndex, width, isLastColumn) => {
        const { isSorted, columnState } = getSortState(column);
        const currentStyles = styles[columnState] || styles.default;

        const columnStyle = {
            padding: `${HEADER_DIMENSIONS.padding.vertical}px`,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: `${width}px`,
            ...(isLastColumn ? { flex: 1 } : { width: `${width}px`, flexShrink: 0 }),
            cursor: column.sortable ? 'pointer' : 'default',
            userSelect: 'none',
            gap: `${HEADER_DIMENSIONS.gap}px`
        };

        const textStyle = {
            ...currentStyles.text
        };

        const sortIconStyle = {
            ...currentStyles.sortIcon,
            size: currentStyles.sortIcon?.size || 'sm'
        };

        const handleClick = () => handleColumnClick(column);

        return rc(
            'div',
            {
                // Web-only component using inline styles

                style: columnStyle,
                onClick: handleClick,
                onDoubleClick: () => handleColumnWidthChange(column),
                key: `header-col-${column.id}`,
                'data-testid': 'column-header',
                'aria-sort': column.sortable && isSorted ? (isSorted === 'asc' ? 'ascending' : 'descending') : 'none'
            },
            // Sort indicator (before text)
            column.sortable &&
                isSorted &&
                rc(BaseIcon, {
                    name: isSorted === 'asc' ? 'arrow-up' : 'arrow-down',
                    size: sortIconStyle.size,
                    color: sortIconStyle.color,
                    key: 'sort-icon'
                }),
            // Column label
            rc('span', { style: textStyle }, column.label || column.title)
        );
    };

    const renderColumns = () => {
        // First pass: calculate total number of columns after expansion
        let totalColumns = 0;
        columnHNodes.forEach(column => {
            if (column.hNodeType === 'PatchDetail') {
                totalColumns += getNestedPatchDetailColumnHNodes().length;
            } else {
                totalColumns += 1;
            }
        });

        // Second pass: render columns with correct indices
        let cumulativeIndex = 0;
        return columnHNodes.flatMap((column, index) => {
            let columns = [column];
            const width = columnWidths[index] || COLUMN_WIDTHS.standard;
            const isPatchDetail = column.hNodeType === 'PatchDetail';

            if (isPatchDetail) {
                columns = getNestedPatchDetailColumnHNodes();
            }

            const result = columns.map((c, j) => {
                const columnWidth = isPatchDetail ? c.width : width;
                const columnIndex = cumulativeIndex + j;
                const isLastColumn = columnIndex === totalColumns - 1;
                return renderHeaderColumn(c, columnIndex, columnWidth, isLastColumn);
            });

            cumulativeIndex += columns.length;
            return result;
        });
    };

    return rc(
        'div',
        {
            style: headerContainerStyle,
            'data-testid': 'table-header-gl2025'
        },
        renderCheckboxCell(),
        ...renderColumns()
    );
}
